import type { CSSProperties } from 'react';
import { useSecondaryColor } from '@vibesites/runtime/react';

// ============================================================================
// Canonical CTA styling — single source of truth
// ============================================================================
//
// Every call-to-action button in the theme shares ONE geometry + typography +
// hover treatment, defined once in <CtaStyles/> below. There are exactly two
// visual types:
//
//   • CTA_OUTLINE — transparent body, accent border + label, fills with the
//                   accent on hover (label flips to the on-accent color).
//   • CTA_SOLID   — filled with the accent at rest, darkens slightly on hover.
//
// Color is NOT baked into the classes — it adapts per surface through two CSS
// custom properties the classes read:
//
//   --cta-accent     border + text at rest; the fill color on hover/at-rest
//   --cta-on-accent  the text color once the accent fill is showing
//
// Drop one of the surface presets below onto the button's (or an ancestor's)
// `style` so the same button reads correctly over a light card, the primary
// brand color, or a dark photo. This keeps the two button types looking the
// same everywhere while staying legible against any brand palette.

export const CTA_OUTLINE = 'v-cta';
export const CTA_SOLID = 'v-cta v-cta--solid';
export const CTA_DISABLED = 'v-cta v-cta--disabled';

/** Secondary-fill hover modifier — the canonical PropertyOverview treatment.
 *  Add alongside CTA_SOLID / CTA_OUTLINE to swap the default hover for a fill
 *  with the SECONDARY brand color and a white label (see .v-cta--surface-hover
 *  below). The fill color is resolved once by <CtaStyles/> into the
 *  `--cta-hover-fill` custom property: it's the raw secondary when white labels
 *  clear the WCAG-AA contrast bar on it, or an auto-darkened shade of secondary
 *  when they don't. The class name is kept for backwards compatibility even
 *  though the fill is no longer the surface color. */
export const CTA_SURFACE_HOVER = 'v-cta--surface-hover';

/** Light/neutral surface (white cards, light sections): solid/outline in the
 *  primary brand color. */
export const ctaOverLight = {
  '--cta-accent': 'var(--vibe-color-primary)',
  '--cta-on-accent': 'var(--vibe-color-text-on-primary)',
} as CSSProperties;

/** Primary-colored surface (e.g. the footer / primary hero): invert so the
 *  button reads as the on-primary token against the brand background. */
export const ctaOverPrimary = {
  '--cta-accent': 'var(--vibe-color-text-on-primary)',
  '--cta-on-accent': 'var(--vibe-color-primary)',
} as CSSProperties;

/** Dark photo / image overlay: force white so the button reads regardless of
 *  the property's primary color. */
export const ctaOverPhoto = {
  '--cta-accent': '#ffffff',
  '--cta-on-accent': 'var(--vibe-color-primary)',
} as CSSProperties;

/** Build a surface preset from explicit (e.g. luminance-picked) colors. */
export function ctaSurface(accent: string, onAccent: string): CSSProperties {
  return {
    '--cta-accent': accent,
    '--cta-on-accent': onAccent,
  } as CSSProperties;
}

const CTA_CSS = `
  .v-cta {
    position: relative;
    z-index: 0;
    overflow: hidden;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    font-family: var(--vibe-font-body);
    /* Canonical CTA size — the single source of truth for EVERY call-to-action
       in the theme. Keeping font-size, line-height, vertical padding and
       min-width here means all CTAs render at one consistent size site-wide;
       the Hero CTA is the sole exception (it only adds uppercase — see
       .hero-cta in Hero.tsx). Vertical padding subtracts the 1.5px border so a
       solid (border-less-looking) button matches an outline button's height,
       and min-width snaps shorter labels to a common baseline width. */
    font-size: 1rem;
    font-weight: 600;
    line-height: 1.2;
    /* Caps are NOT applied globally — only the Hero CTA sits over the photo and
       keeps the uppercase treatment (see .hero-cta in Hero.tsx). Every other
       CTA in the theme renders in its label's natural case. */
    letter-spacing: 0.2em;
    text-align: center;
    padding: calc(0.9rem - 1.5px) 1.75rem;
    min-width: 13rem;
    border-radius: var(--vibe-radius-sm, 2px);
    border: 1.5px solid var(--cta-accent, var(--vibe-color-primary));
    color: var(--cta-accent, var(--vibe-color-primary));
    background: transparent;
    text-decoration: none;
    cursor: pointer;
    box-shadow: none;
    transition: color 0.3s ease, background 0.3s ease;
  }
  .v-cta::before {
    content: '';
    position: absolute;
    inset: 0;
    z-index: -1;
    background: var(--cta-accent, var(--vibe-color-primary));
    opacity: 0;
    transition: opacity 0.3s ease;
  }
  /* Keep any wrapper span (label + icon) above the ::before fill. */
  .v-cta > * { position: relative; }
  .v-cta-label {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
  }
  /* Hover is background-only — the fill changes, no lift or shadow. */
  .v-cta:hover {
    color: var(--cta-on-accent, var(--vibe-color-text-on-primary));
  }
  .v-cta:hover::before { opacity: 1; }

  /* Solid — filled with the accent at rest, darkens on hover. */
  .v-cta--solid {
    background: var(--cta-accent, var(--vibe-color-primary));
    color: var(--cta-on-accent, var(--vibe-color-text-on-primary));
  }
  .v-cta--solid::before { background: #000; }
  .v-cta--solid:hover { color: var(--cta-on-accent, var(--vibe-color-text-on-primary)); }
  .v-cta--solid:hover::before { opacity: 0.28; }

  /* Non-interactive / unavailable. */
  .v-cta--disabled,
  .v-cta--disabled:hover {
    opacity: 0.5;
    cursor: default;
    pointer-events: none;
    transform: none;
    box-shadow: none;
  }
  .v-cta--disabled:hover::before { opacity: 0; }

  /* Secondary-fill hover — the canonical PropertyOverview CTA treatment. On
     hover the button fills background + border with the resolved hover fill
     (the --cta-hover-fill variable: the secondary brand color, auto-darkened
     when a white label wouldn't clear WCAG AA against it — see pickCtaHoverFill)
     and keeps the label white, and the solid darkening overlay is suppressed so the fill
     shows cleanly. Works on both the solid and outline variants. Declared last
     so it wins on source order against the base :hover rules at equal
     specificity. */
  .v-cta--surface-hover:hover {
    color: #ffffff;
    background: var(--cta-hover-fill, var(--vibe-color-secondary));
    border-color: var(--cta-hover-fill, var(--vibe-color-secondary));
  }
  .v-cta--surface-hover:hover::before { opacity: 0; }
`;

// ============================================================================
// Secondary-fill hover contrast resolution
// ----------------------------------------------------------------------------
// The `.v-cta--surface-hover` treatment fills the button with the SECONDARY
// brand color on hover and paints the label white. Whether white is legible on
// the raw secondary depends entirely on the property's palette — a deep teal
// secondary is fine, a pale gold is not. `pickCtaHoverFill` decides per palette:
// keep the secondary when a white label clears WCAG AA (4.5:1) against it, or
// return the minimally-darkened shade of secondary that does. The result is
// published once (by <CtaStyles/>) as the `--cta-hover-fill` CSS variable that
// the hover rules — canonical and the per-component copies — read.
// ============================================================================

// White is the label color on the secondary hover fill; used as the reference
// for every contrast check below.
const CTA_HOVER_LABEL_LUMINANCE = 1; // relative luminance of #ffffff
// WCAG AA for normal-size text. CTA labels (1rem / 600) don't qualify as "large
// text", so we hold them to the stricter 4.5:1 bar.
const CTA_HOVER_MIN_CONTRAST = 4.5;

function ctaHexToRgb(hex: string): { r: number; g: number; b: number } | null {
  const cleaned = hex.replace('#', '').trim();
  if (cleaned.length === 3) {
    return {
      r: parseInt(cleaned[0] + cleaned[0], 16),
      g: parseInt(cleaned[1] + cleaned[1], 16),
      b: parseInt(cleaned[2] + cleaned[2], 16),
    };
  }
  if (cleaned.length === 6) {
    return {
      r: parseInt(cleaned.slice(0, 2), 16),
      g: parseInt(cleaned.slice(2, 4), 16),
      b: parseInt(cleaned.slice(4, 6), 16),
    };
  }
  return null;
}

function ctaRgbToHex(r: number, g: number, b: number): string {
  const toHex = (n: number) => Math.max(0, Math.min(255, Math.round(n))).toString(16).padStart(2, '0');
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

function ctaRelativeLuminance(rgb: { r: number; g: number; b: number }): number {
  const [rs, gs, bs] = [rgb.r / 255, rgb.g / 255, rgb.b / 255].map((c) =>
    c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4)
  );
  return 0.2126 * rs + 0.7152 * gs + 0.0722 * bs;
}

/** WCAG contrast ratio between a background luminance and the white label. */
function ctaContrastWithWhite(bgLuminance: number): number {
  const lighter = Math.max(CTA_HOVER_LABEL_LUMINANCE, bgLuminance);
  const darker = Math.min(CTA_HOVER_LABEL_LUMINANCE, bgLuminance);
  return (lighter + 0.05) / (darker + 0.05);
}

/**
 * Resolve the CTA secondary-hover fill for a given secondary color.
 *
 * Returns the secondary color unchanged when a white label already clears
 * {@link CTA_HOVER_MIN_CONTRAST} against it; otherwise returns the minimally
 * darkened shade of secondary (mixed toward black in small steps) that does.
 * Falls back to the raw input if the color can't be parsed. Darkening toward
 * black only ever raises contrast against white, so a passing shade always
 * exists within the search range.
 */
export function pickCtaHoverFill(secondary: string | null | undefined): string {
  if (!secondary) return 'var(--vibe-color-secondary)';
  const rgb = ctaHexToRgb(secondary);
  if (!rgb) return secondary;

  // Step 0 is the untouched secondary; each step blends 4% more black in. Cap
  // at 90% black — by then contrast against white is far past the threshold for
  // any hue, so the loop effectively always resolves before the cap.
  for (let blackPct = 0; blackPct <= 0.9; blackPct += 0.04) {
    const f = 1 - blackPct;
    const shade = { r: rgb.r * f, g: rgb.g * f, b: rgb.b * f };
    if (ctaContrastWithWhite(ctaRelativeLuminance(shade)) >= CTA_HOVER_MIN_CONTRAST) {
      return blackPct === 0 ? secondary : ctaRgbToHex(shade.r, shade.g, shade.b);
    }
  }
  // Deepest shade we searched — guaranteed high-contrast against white.
  const f = 0.1;
  return ctaRgbToHex(rgb.r * f, rgb.g * f, rgb.b * f);
}

/** Injects the canonical CTA stylesheet plus the resolved `--cta-hover-fill`
 *  custom property (the secondary hover fill, contrast-corrected against the
 *  white label — see {@link pickCtaHoverFill}). Render once inside any component
 *  that uses CTA_OUTLINE / CTA_SOLID. Duplicate identical <style> tags across
 *  sections are harmless. */
export function CtaStyles() {
  const secondary = useSecondaryColor();
  const hoverFill = pickCtaHoverFill(secondary);
  return <style>{`:root { --cta-hover-fill: ${hoverFill}; }\n${CTA_CSS}`}</style>;
}
